import React, { useEffect, useState } from 'react';
import { cn } from '../ui';

export type PlayerService = 'apple' | 'spotify' | 'preview';

const PREF_KEY = 'audioServicePreference';

export function getStoredServicePreference(): PlayerService {
  if (typeof window === 'undefined') return 'preview';
  const v = window.localStorage.getItem(PREF_KEY);
  return v === 'apple' || v === 'spotify' || v === 'preview' ? v : 'apple';
}

export function setStoredServicePreference(p: PlayerService): void {
  try { window.localStorage.setItem(PREF_KEY, p); } catch { /* ignore */ }
}

export interface EmbeddedPlayerSources {
  appleTrackId?: string;     // Apple Music numeric track id
  appleCollectionId?: string; // Apple Music album/collection id (required for the embed URL)
  spotifyTrackId?: string;
  previewUrl?: string;
  durationMs?: number;
  title?: string;
  artist?: string;
}

export interface EmbeddedPlayerProps {
  sources: EmbeddedPlayerSources;
  className?: string;
}

/** Resolve the actual service to render given user preference + which sources are available. */
function resolveService(
  pref: PlayerService,
  s: EmbeddedPlayerSources
): PlayerService | null {
  const ok: Record<PlayerService, boolean> = {
    // Apple Music's `embed.music.apple.com/{cc}/song/{trackId}` works without an
    // album ID — so we only need the track ID.
    apple: !!s.appleTrackId,
    spotify: !!s.spotifyTrackId,
    preview: !!s.previewUrl,
  };
  if (ok[pref]) return pref;
  // fallback chain
  for (const next of ['apple', 'spotify', 'preview'] as PlayerService[]) {
    if (ok[next]) return next;
  }
  return null;
}

const SERVICE_LABEL: Record<PlayerService, string> = {
  apple: 'Apple Music',
  spotify: 'Spotify',
  preview: 'Preview',
};

export const EmbeddedPlayer: React.FC<EmbeddedPlayerProps> = ({ sources, className }) => {
  const [pref, setPref] = useState<PlayerService>(getStoredServicePreference());

  useEffect(() => { setStoredServicePreference(pref); }, [pref]);

  const active = resolveService(pref, sources);

  const available: Record<PlayerService, boolean> = {
    apple: !!sources.appleTrackId,
    spotify: !!sources.spotifyTrackId,
    preview: !!sources.previewUrl,
  };

  const noneAvailable = !available.apple && !available.spotify && !available.preview;

  if (noneAvailable) {
    return (
      <div className={cn('rounded-2xl border border-light bg-bg-2 px-5 py-4 text-text-secondary text-small', className)}>
        Audio not available for this bhajan.
      </div>
    );
  }

  return (
    <div className={cn('rounded-2xl border border-light bg-bg-2 p-4', className)}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-small text-text-tertiary">Listen on</span>
        <div role="radiogroup" aria-label="Audio service" className="inline-flex rounded-full border border-light bg-bg-3 p-0.5">
          {(['apple', 'spotify', 'preview'] as PlayerService[]).map((s) => {
            const isActive = pref === s;
            const isAvail = available[s];
            return (
              <button
                key={s}
                role="radio"
                aria-checked={isActive}
                disabled={!isAvail}
                onClick={() => setPref(s)}
                className={cn(
                  'px-3 h-8 rounded-full text-small font-medium transition-colors duration-150',
                  isActive
                    ? 'bg-accent-gold text-bg-0'
                    : isAvail
                      ? 'text-text-secondary hover:text-text-primary'
                      : 'text-text-tertiary opacity-40 cursor-not-allowed'
                )}
              >
                {SERVICE_LABEL[s]}
              </button>
            );
          })}
        </div>
        {active && active !== pref && (
          <span className="text-[11px] text-text-tertiary italic">
            {SERVICE_LABEL[pref]} unavailable for this bhajan — using {SERVICE_LABEL[active]}.
          </span>
        )}
      </div>

      {active === 'apple' && sources.appleTrackId && (
        <iframe
          title={`Apple Music: ${sources.title ?? 'bhajan'}`}
          allow="autoplay *; encrypted-media *; clipboard-write *; fullscreen *"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-presentation"
          className="w-full rounded-xl"
          height={175}
          loading="lazy"
          src={
            sources.appleCollectionId
              ? `https://embed.music.apple.com/us/album/_/${sources.appleCollectionId}?i=${sources.appleTrackId}&theme=dark`
              : `https://embed.music.apple.com/us/song/${sources.appleTrackId}?theme=dark`
          }
        />
      )}

      {active === 'spotify' && sources.spotifyTrackId && (
        <iframe
          title={`Spotify: ${sources.title ?? 'bhajan'}`}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          referrerPolicy="no-referrer-when-downgrade"
          loading="lazy"
          className="w-full rounded-xl"
          height={152}
          src={`https://open.spotify.com/embed/track/${sources.spotifyTrackId}`}
        />
      )}

      {active === 'preview' && sources.previewUrl && (
        <PreviewPlayer url={sources.previewUrl} title={sources.title} artist={sources.artist} />
      )}
    </div>
  );
};

/** Custom audio for the in-house preview clip. Connects to the global AudioPlayerProvider via a small hook callback path. */
const PreviewPlayer: React.FC<{ url: string; title?: string; artist?: string }> = ({ url, title, artist }) => {
  return (
    <div className="flex items-center gap-3">
      <audio controls preload="metadata" src={url} className="flex-1 h-10" style={{ colorScheme: 'dark' }} />
      <div className="hidden sm:flex flex-col text-small text-text-tertiary">
        <span className="truncate max-w-[200px]">{title}</span>
        <span className="truncate max-w-[200px]">{artist}</span>
      </div>
    </div>
  );
};
